import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/lib/sessionCreationGate";
import "@/lib/interviewScrollReset";
import "@/lib/compactUiCopy";
import {
  checkActiveInterviewBeforeSetup,
  installActiveInterviewSetupGuard,
} from "@/lib/activeInterviewSetupGuard";
import {
  checkSavedInterviewBeforeSetup,
  installSavedInterviewLifecycle,
} from "@/lib/savedInterviewLifecycle";
import App from "@/App";
import "@/mobile-responsive.css";
import "@/responsive-polish.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

async function resolveInitialAuthenticatedUser() {
  try {
    const response = await fetch("/api/auth/me", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data?.user || null;
  } catch {
    return null;
  }
}

async function bootstrap() {
  // Resolve an existing cookie-backed session before rendering the launch
  // experience. Returning authenticated users should enter the app directly;
  // the launch video is reserved for a genuine unauthenticated app entry.
  const initialUser = await resolveInitialAuthenticatedUser();
  window.__LM_INITIAL_AUTH_USER__ = initialUser;

  // A returning admin who opens the app root should land on the Admin
  // Dashboard just as they do after an explicit admin login.
  if (initialUser?.is_admin && window.location.pathname === "/") {
    const next = `/admin${window.location.search || ""}${window.location.hash || ""}`;
    window.history.replaceState(window.history.state, "", next);
  }

  installActiveInterviewSetupGuard();
  installSavedInterviewLifecycle();

  const activeInterviewClear = await checkActiveInterviewBeforeSetup();
  if (!activeInterviewClear) return;

  const savedInterviewClear = await checkSavedInterviewBeforeSetup();
  if (!savedInterviewClear) return;

  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

bootstrap();
